// src/components/UserPicker.tsx
// GitLab-style user picker dropdown with search, avatars, and display names.
//
// Who can be picked depends on where you are. Assigning a task inside a project
// should offer that project's members, not every account on the platform, so the
// picker takes an optional `scope` — the parent entity the surrounding form is
// creating under — and lists the profiles that hold its membership relation.
//
// Without a scope there is no short, meaningful set to draw from, so the picker
// opens on people you have picked before (and whatever the host page knows to
// be relevant, via `suggestions`) and otherwise asks you to type. It never lists
// the platform: a page of two hundred strangers is not an answer to "who do you
// mean", it is the question asked back. Typing runs `SearchUserprofiles` on the
// server — debounced, paged, and loaded further as you scroll — because the
// server is the only thing that has seen everyone.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { authzApi, userprofileApi } from '@core/api';
import { ListProjectMembers } from '@core/api/endpoints/project/list_project_members';
import { EntityAvatar } from './EntityAvatar';
import {
  candidateFromProfile,
  candidateFromSearchResult,
  candidateHasId,
  candidateKey,
  matchesQuery,
  mergeCandidates,
  readRecentPicks,
  rememberPick,
  type PickerCandidate,
} from '@/features/userprofile/pickerCandidates';
import { AnchoredMenu } from './AnchoredMenu';

export type { PickerCandidate } from '@/features/userprofile/pickerCandidates';

/** A page small enough to render instantly and to be worth scrolling past. */
const SEARCH_PAGE_SIZE = 25;
/** Same as TagPicker — one request per pause, not per keystroke. */
const SEARCH_DEBOUNCE_MS = 250;
/** How close to the bottom counts as "asking for the next page". */
const LOAD_MORE_THRESHOLD_PX = 64;

/**
 * The entity the surrounding form is working under. Supported types narrow
 * the candidate list; any other type (or none) leaves it unscoped, because
 * inventing a membership relation that the backend does not have would offer a
 * list nobody can trust.
 */
export interface UserPickerScope {
  /** Entity type name — 'project', 'team', 'document', 'task', or 'platform'. */
  typeName: string;
  id: string;
  /** How that entity reads in prose, for the "showing members of …" line. */
  label?: string;
}

interface UserPickerProps {
  value: string;
  onChange: (userId: string, displayName: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  /** When set, shows project members instead of searching everyone */
  projectId?: string;
  /** When set, shows team members instead of searching everyone */
  teamId?: string;
  /** Parent entity to scope the list by — an alternative to projectId/teamId. */
  scope?: UserPickerScope;
  /** Never widen a recipient list to platform-wide search or recent picks. */
  restrictToScope?: boolean;
  /** Document permissions address profiles; most other forms address users. */
  valueKind?: 'user' | 'profile';
  /**
   * People a multi-select host has already taken. They stay on screen, marked
   * as added — dropping them would make the list shuffle under the cursor after
   * every pick — but they cannot be picked twice.
   */
  selectedIds?: string[];
  /**
   * What the host page knows to be relevant before anything is typed — recent
   * chat partners, say. Shown above the picker's own recents.
   */
  suggestions?: PickerCandidate[];
}

/**
 * Labels for a `value` the picker did not hand out itself (a task loaded with an
 * assignee already on it). Module-level so that the dozen pickers on a board
 * resolve each person once, not once per instance.
 */
const resolvedByValue = new Map<string, PickerCandidate>();

/**
 * Resolve an id the caller stored back into a person. Call sites hold user ids
 * (that is what `onChange` emits) but some hold profile ids, and only the
 * backend can tell them apart — so ask by profile id first and fall back to the
 * user's default profile.
 */
async function resolveValue(id: string): Promise<PickerCandidate | null> {
  const cached = resolvedByValue.get(id);
  if (cached) return cached;
  try {
    const byId = await userprofileApi.ListUserprofilesByIDs({ ids: [id] });
    const profile = byId.userprofiles?.profiles?.[0];
    if (profile) {
      const c = candidateFromProfile(profile);
      resolvedByValue.set(id, c);
      return c;
    }
  } catch {
    // Fall through — an id that is not a profile id is the expected case.
  }
  try {
    const byUser = await userprofileApi.GetUserprofileDefault(id);
    if (byUser.userprofile) {
      const c = candidateFromProfile(byUser.userprofile);
      resolvedByValue.set(id, c);
      return c;
    }
  } catch {
    // Unknown or invisible to us. The trigger keeps its placeholder.
  }
  return null;
}

export const UserPicker = observer(({
  value,
  onChange,
  label,
  placeholder,
  error,
  className = '',
  projectId,
  teamId,
  scope,
  restrictToScope = Boolean(projectId || teamId || (scope?.id && ['project', 'team', 'document', 'task', 'platform'].includes(scope.typeName))),
  valueKind = 'user',
  selectedIds,
  suggestions,
}: UserPickerProps) => {
  const { t } = useI18n();

  // An explicit projectId/teamId wins over `scope`; both express the same idea,
  // and the explicit one is the caller being specific on purpose.
  const scopedProjectId = projectId || (scope?.typeName === 'project' ? scope.id : '') || '';
  const scopedTeamId = teamId || (scope?.typeName === 'team' ? scope.id : '') || '';
  const scopedDocumentId = scope?.typeName === 'document' ? scope.id : '';
  const scopedMembershipObject = scope?.id && (scope.typeName === 'task' || scope.typeName === 'platform')
    ? `${scope.typeName}:${scope.id}` : '';
  const scopeLabel = scope?.label || '';

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scopedMembers, setScopedMembers] = useState<PickerCandidate[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(false);
  /** Set when the person deliberately stepped out of an empty scoped list. */
  const [searchEveryone, setSearchEveryone] = useState(false);
  const [membersNonce, setMembersNonce] = useState(0);
  const [searchNonce, setSearchNonce] = useState(0);

  const [recents, setRecents] = useState<PickerCandidate[]>([]);
  const [results, setResults] = useState<PickerCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [valueCandidate, setValueCandidate] = useState<PickerCandidate | null>(null);

  const hasScope = !!(scopedProjectId || scopedTeamId || scopedDocumentId || scopedMembershipObject) && !searchEveryone;
  const query = search.trim();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  /** Guards against a slow early page overwriting a later one. */
  const searchSeq = useRef(0);
  // Scroll fires far faster than React commits state, so "am I already fetching
  // the next page" has to be answered synchronously or one flick asks twice.
  const loadingMoreRef = useRef(false);

  // A different scope is a different list — never show the previous one while
  // the new one loads, and never keep a widened list across a scope change.
  useEffect(() => {
    setScopedMembers([]);
    setMembersError(false);
    setSearchEveryone(false);
  }, [scopedProjectId, scopedTeamId, scopedDocumentId, scopedMembershipObject]);

  useEffect(() => {
    if (!scopedDocumentId || !isOpen) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    authzApi.ListUsersByObject({
      object: `document:${scopedDocumentId}`,
      relation: 'viewer',
      user_filters: { items: [{ type: 'profile', relation: '' }] },
    }).then(async (response) => {
      const ids = (response.users?.items ?? [])
        .map((entry) => entry.user_id?.replace(/^profile:/, '') || '')
        .filter(Boolean);
      const profiles = ids.length ? await userprofileApi.ListUserprofilesByIDs({ ids }) : null;
      if (!cancelled) setScopedMembers((profiles?.userprofiles?.profiles ?? []).map(candidateFromProfile));
    }).catch(() => {
      if (!cancelled) setMembersError(true);
    }).finally(() => {
      if (!cancelled) setMembersLoading(false);
    });
    return () => { cancelled = true; };
  }, [scopedDocumentId, isOpen, membersNonce]);

  useEffect(() => {
    if (!scopedMembershipObject || !isOpen) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    authzApi.ListUsersByObject({
      object: scopedMembershipObject,
      relation: 'member',
      user_filters: { items: [{ type: 'profile', relation: '' }] },
    }).then(async (response) => {
      const ids = (response.users?.items ?? [])
        .map(entry => entry.user_id?.replace(/^profile:/, '') || '')
        .filter(Boolean);
      const profiles = ids.length ? await userprofileApi.ListUserprofilesByIDs({ ids }) : null;
      if (!cancelled) setScopedMembers((profiles?.userprofiles?.profiles ?? []).map(candidateFromProfile));
    }).catch(() => { if (!cancelled) setMembersError(true); })
      .finally(() => { if (!cancelled) setMembersLoading(false); });
    return () => { cancelled = true; };
  }, [scopedMembershipObject, isOpen, membersNonce]);

  // Project members carry only a profile reference, so their names come from one
  // batched profile read for exactly those ids — never from a list of everyone.
  useEffect(() => {
    if (!scopedProjectId || !isOpen) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    ListProjectMembers(scopedProjectId)
      .then(async (response) => {
        const members = response?.members?.members ?? [];
        const ids = members.map(m => m.profile?.id).filter((id): id is string => !!id);
        let hydrated: PickerCandidate[] = [];
        if (ids.length > 0) {
          const profiles = await userprofileApi.ListUserprofilesByIDs({ ids });
          hydrated = (profiles.userprofiles?.profiles ?? []).map(candidateFromProfile);
        }
        if (cancelled) return;
        // Keep membership order, and keep a member whose profile we could not
        // read: they are in the project whether or not we can name them.
        const byProfileId = new Map(hydrated.map(c => [c.profileId, c]));
        setScopedMembers(members.map((m) => {
          const pid = m.profile?.id ?? '';
          return byProfileId.get(pid) ?? { profileId: pid, displayName: t('chat.unknownUser'), secondary: '' };
        }).filter(c => !!candidateKey(c)));
      })
      .catch(() => {
        if (!cancelled) setMembersError(true);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedProjectId, isOpen, membersNonce]);

  // Team membership is granted to profiles. The team list endpoint presents
  // users, which can lose the exact profile that holds the grant.
  useEffect(() => {
    if (!scopedTeamId || !isOpen) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    authzApi.ListUsersByObject({
      object: `team:${scopedTeamId}`,
      relation: 'member',
      user_filters: { items: [{ type: 'profile', relation: '' }] },
    })
      .then(async (response) => {
        if (cancelled) return;
        const ids = (response.users?.items ?? [])
          .map(entry => entry.user_id?.replace(/^profile:/, '') || '')
          .filter(Boolean);
        const profiles = ids.length ? await userprofileApi.ListUserprofilesByIDs({ ids }) : null;
        if (!cancelled) setScopedMembers((profiles?.userprofiles?.profiles ?? []).map(candidateFromProfile));
      })
      .catch(() => {
        if (!cancelled) setMembersError(true);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedTeamId, isOpen, membersNonce]);

  // Recents are read on open rather than on mount: a form can hold a dozen
  // pickers, and reading localStorage for all of them up front buys nothing.
  useEffect(() => {
    if (isOpen) setRecents(readRecentPicks());
  }, [isOpen]);

  // Debounced server search. One request per pause in typing, and none at all
  // while the box is empty or a scoped member list is already on screen.
  useEffect(() => {
    if (!isOpen || hasScope || restrictToScope) return;
    if (!query) {
      searchSeq.current += 1;
      setResults([]);
      setNextCursor('');
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }
    const seq = ++searchSeq.current;
    setIsSearching(true);
    setSearchFailed(false);
    const timer = setTimeout(async () => {
      try {
        const res = await userprofileApi.SearchUserprofiles({ q: query, limit: SEARCH_PAGE_SIZE });
        if (seq !== searchSeq.current) return;
        setResults((res.results?.results ?? []).map(candidateFromSearchResult));
        setNextCursor(res.pagination?.page_info?.has_next ? (res.pagination.page_info.next_cursor || '') : '');
      } catch {
        if (seq !== searchSeq.current) return;
        setResults([]);
        setNextCursor('');
        setSearchFailed(true);
      } finally {
        if (seq === searchSeq.current) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, isOpen, hasScope, restrictToScope, searchNonce]);

  const loadMore = useCallback(async () => {
    if (restrictToScope || !nextCursor || loadingMoreRef.current || isSearching) return;
    const seq = searchSeq.current;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const res = await userprofileApi.SearchUserprofiles({
        q: query,
        limit: SEARCH_PAGE_SIZE,
        cursor: nextCursor,
        direction: 'forward',
      });
      if (seq !== searchSeq.current) return;
      const page = (res.results?.results ?? []).map(candidateFromSearchResult);
      setResults(prev => mergeCandidates(prev, page));
      setNextCursor(res.pagination?.page_info?.has_next ? (res.pagination.page_info.next_cursor || '') : '');
    } catch {
      if (seq === searchSeq.current) setNextCursor('');
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [nextCursor, isSearching, query, restrictToScope]);

  const handleResultsScroll = useCallback(() => {
    const el = resultsRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_THRESHOLD_PX) loadMore();
  }, [loadMore]);

  // What is on offer, and why. Three distinct sets, never silently blended:
  // a scoped membership, a typed search, or the short "people you deal with".
  const suggested = useMemo(
    () => restrictToScope ? [] : mergeCandidates(suggestions ?? [], recents),
    [suggestions, recents, restrictToScope],
  );

  const items = useMemo(() => {
    if (hasScope) return scopedMembers.filter(c => matchesQuery(c, query));
    return restrictToScope ? [] : query ? results : suggested;
  }, [hasScope, scopedMembers, query, results, suggested, restrictToScope]);

  const takenIds = useMemo(() => selectedIds ?? [], [selectedIds]);
  const isTaken = useCallback(
    (c: PickerCandidate) => takenIds.some(id => candidateHasId(c, id)),
    [takenIds],
  );

  // Focus search on open; forget the query on close so the next open starts on
  // suggestions rather than on someone else's stale search.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  // Close on Escape. Bound on the document rather than on a wrapping <div>,
  // because the dropdown is portaled out of it — a container handler would
  // both miss the key and make a plain container pretend to be interactive.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // This picker often sits inside another popover; it closes first.
      e.stopPropagation();
      setIsOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // The trigger has to name whoever `value` points at even when that person is
  // in no list we hold — a task opened with an assignee on it, for instance.
  useEffect(() => {
    if (!value) {
      setValueCandidate(null);
      return;
    }
    const known = [...suggested, ...scopedMembers, ...results].find(c => candidateHasId(c, value));
    if (known) {
      setValueCandidate(known);
      return;
    }
    if (valueCandidate && candidateHasId(valueCandidate, value)) return;
    let cancelled = false;
    resolveValue(value).then((c) => {
      if (!cancelled && c) setValueCandidate(c);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, suggested, scopedMembers, results]);

  const displayName = valueCandidate?.displayName ?? '';
  const secondary = valueCandidate?.secondary ?? '';

  const handleSelect = async (candidate: PickerCandidate) => {
    setIsOpen(false);
    // Callers store the user id — resolve it now for a search hit, which is
    // privacy-projected and carries only the profile. Remembering the resolved
    // pair means picking the same person again costs no request at all.
    let picked = candidate;
    if (valueKind === 'user' && !picked.userId && picked.profileId) {
      const full = await resolveValue(picked.profileId);
      if (full) picked = mergeCandidates([full], [candidate])[0];
    }
    const id = valueKind === 'profile' ? picked.profileId : picked.userId || picked.profileId;
    if (!id) return;
    resolvedByValue.set(id, picked);
    setValueCandidate(picked);
    setRecents(rememberPick(picked));
    onChange(id, picked.displayName);
  };

  // Which set is on offer — never left to be guessed. Silent only when there is
  // no set at all yet: labelling an empty box "Recent" says nothing true.
  const sourceLine = hasScope
    ? scopedDocumentId
      ? t('userPicker.fromDocument')
      : scopedMembershipObject
      ? t(scope?.typeName === 'platform' ? 'userPicker.fromPlatform' : 'userPicker.fromTask')
      : scopedProjectId
      ? (scopeLabel ? t('userPicker.fromProject', { name: scopeLabel }) : t('userPicker.fromProjectGeneric'))
      : (scopeLabel ? t('userPicker.fromTeam', { name: scopeLabel }) : t('userPicker.fromTeamGeneric'))
    : query
      ? t('userPicker.searchResults')
      : suggested.length > 0
        ? t('userPicker.recentPeople')
        : '';

  const showSpinner = hasScope ? membersLoading : isSearching;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger. Clearing is a button beside it, not a click target painted on
          an icon: an <svg onClick> inside a <button> is unreachable by keyboard
          and announces nothing. */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`
            w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors
            ${error
              ? 'border-destructive focus:ring-destructive'
              : 'border-input focus:ring-ring'
            }
            ${isOpen ? 'ring-2 ring-ring' : ''}
            ${value ? 'pr-9' : ''}
            bg-card text-foreground hover:bg-muted/50
          `}
        >
          {value ? (
            <>
              <EntityAvatar name={displayName} image={valueCandidate?.avatar} profileId={valueCandidate?.profileId} size="xs" className="h-7 w-7" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate leading-tight">
                  {displayName || t('common.loading')}
                </p>
                {secondary && (
                  <p className="text-xs text-muted-foreground truncate leading-tight">
                    {secondary}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <svg
                className="h-5 w-5 text-muted-foreground flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="flex-1 text-muted-foreground">
                {placeholder || t('task.field.assigneePlaceholder')}
              </span>
              <svg
                className="h-4 w-4 text-muted-foreground flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </>
          )}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('', '')}
            aria-label={t('common.clear')}
            title={t('common.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}

      {/* Dropdown via portal */}
      <AnchoredMenu
        anchor={buttonRef}
        ignoreRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        role="none"
        matchTriggerWidth
        minWidth={320}
        maxHeight={320}
        // A flex column, so the result list gets a real height to scroll inside.
        // A percentage max-height against the menu's own max-height resolves to
        // nothing, which is how a long list ended up clipped and unscrollable.
        className="flex flex-col overflow-hidden"
      >
        {/* Search */}
        <div className="flex-shrink-0 p-2 border-b border-border">
          <div className="relative">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder={t('userPicker.searchPlaceholder')}
            />
          </div>
        </div>

        {sourceLine && (
          <p className="flex-shrink-0 border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
            {sourceLine}
          </p>
        )}

        {/* Results */}
        <div
          ref={resultsRef}
          onScroll={handleResultsScroll}
          className="min-h-0 flex-1 overflow-y-auto py-1"
        >
          {showSpinner ? (
            <div className="px-3 py-4 text-center">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-xs text-muted-foreground mt-1">
                {hasScope ? t('common.loading') : t('userPicker.searching')}
              </p>
            </div>
          ) : (hasScope && membersError) || searchFailed ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-destructive">
                {hasScope ? t('userPicker.loadFailed') : t('userPicker.searchFailed')}
              </p>
              <button
                type="button"
                onClick={() => (hasScope ? setMembersNonce(n => n + 1) : setSearchNonce(n => n + 1))}
                className="mt-1 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : items.length === 0 ? (
            // Three different nothings, and they must not look alike: no match
            // for what you typed, an empty membership, or nothing to suggest yet.
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {query
                  ? t('common.noResults')
                  : hasScope
                    ? t('userPicker.emptyScope')
                    : t('userPicker.startTyping')}
              </p>
              {hasScope && !query && !restrictToScope && (
                <button
                  type="button"
                  onClick={() => setSearchEveryone(true)}
                  className="mt-1 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('userPicker.showEveryone')}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Unassign option */}
              {value && (
                <button
                  type="button"
                  onClick={() => { onChange('', ''); setIsOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                >
                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-sm text-muted-foreground">{t('task.unassigned')}</span>
                </button>
              )}

              {items.map((candidate) => {
                const taken = isTaken(candidate);
                const isCurrent = candidateHasId(candidate, value);
                const checked = taken || isCurrent;

                return (
                  <button
                    key={candidateKey(candidate)}
                    type="button"
                    disabled={taken}
                    onClick={() => handleSelect(candidate)}
                    className={`
                      w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                      ${taken ? 'cursor-default opacity-60' : checked ? 'bg-primary/5' : 'hover:bg-muted'}
                    `}
                  >
                    <EntityAvatar name={candidate.displayName} image={candidate.avatar} profileId={candidate.profileId} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground truncate">{candidate.displayName}</span>
                        {checked && (
                          <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                      </div>
                      {candidate.secondary && (
                        <p className="text-xs text-muted-foreground truncate">{candidate.secondary}</p>
                      )}
                    </div>
                    {taken && (
                      <span className="flex-shrink-0 text-xs text-muted-foreground">{t('userPicker.alreadyAdded')}</span>
                    )}
                  </button>
                );
              })}

              {isLoadingMore && (
                <div className="px-3 py-2 text-center">
                  <div className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>
      </AnchoredMenu>
    </div>
  );
});

export default UserPicker;
